// Schedule configuration component for forms
import { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, Timestamp, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { SCHEDULE_FREQUENCIES, calculateNextDueDate } from '../utils/scheduleUtils';

export default function ScheduleManager({ formId, companyId }) {
  const [schedules, setSchedules] = useState([]);
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [frequency, setFrequency] = useState('weekly');
  const [customDays, setCustomDays] = useState(7);
  const [description, setDescription] = useState('');
  const [windowBefore, setWindowBefore] = useState(0);
  const [windowAfter, setWindowAfter] = useState(3);
  const [allUsers, setAllUsers] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);

  // Load schedules
  useEffect(() => {
    if (!formId || !companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const schedulesRef = collection(db, `companies/${companyId}/forms/${formId}/schedules`);
    const unsubscribe = onSnapshot(
      schedulesRef,
      (snapshot) => {
        const scheduleList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSchedules(scheduleList);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading schedules:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [formId, companyId]);

  // Load users
  useEffect(() => {
    if (!companyId) return;

    const usersRef = collection(db, 'users');
    const unsubscribe = onSnapshot(usersRef, (snapshot) => {
      const userList = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(u => u.companyId === companyId);
      setUsers(userList);
    });

    return () => unsubscribe();
  }, [companyId]);

  // Load groups
  useEffect(() => {
    if (!companyId) return;

    // Groups are stored as an array in the company document
    const loadGroups = async () => {
      try {
        const companyDoc = await getDoc(doc(db, 'companies', companyId));
        if (companyDoc.exists()) {
          const companyData = companyDoc.data();
          const groupsArray = companyData.groups || [];
          // Convert group names to objects with id and name
          const groupList = groupsArray.map((groupName) => ({
            id: groupName, // Use the group name as ID
            name: groupName
          }));
          setGroups(groupList);
        } else {
          setGroups([]);
        }
      } catch (error) {
        console.error('Error loading groups:', error);
        setGroups([]);
      }
    };

    loadGroups();
    
    // Also listen for changes to the company document
    const unsubscribe = onSnapshot(doc(db, 'companies', companyId), (snapshot) => {
      if (snapshot.exists()) {
        const companyData = snapshot.data();
        const groupsArray = companyData.groups || [];
        const groupList = groupsArray.map((groupName) => ({
          id: groupName,
          name: groupName
        }));
        setGroups(groupList);
      }
    });

    return () => unsubscribe();
  }, [companyId]);

  function resetForm() {
    setFrequency('weekly');
    setCustomDays(7);
    setDescription('');
    setWindowBefore(0);
    setWindowAfter(3);
    setAllUsers(true);
    setSelectedUsers([]);
    setSelectedGroups([]);
    setEnabled(true);
    setStartDate(new Date().toISOString().split('T')[0]);
    setEditingSchedule(null);
  }

  function handleEdit(schedule) {
    setEditingSchedule(schedule);
    setFrequency(schedule.frequency || 'weekly');
    setCustomDays(schedule.customDays || 7);
    setDescription(schedule.description || '');
    setWindowBefore(schedule.windowBefore || 0);
    setWindowAfter(schedule.windowAfter || 3);
    setAllUsers(schedule.allUsers !== false);
    setSelectedUsers(schedule.assignedUsers || []);
    setSelectedGroups(schedule.assignedGroups || []);
    setEnabled(schedule.enabled !== false);
    setStartDate(schedule.nextDueDate ? schedule.nextDueDate.toDate().toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
    setShowForm(true);
  }

  async function handleSave() {
    try {
      const nextDueDate = Timestamp.fromDate(new Date(startDate));
      
      const scheduleData = {
        frequency,
        customDays: frequency === 'custom' ? customDays : null,
        description,
        windowBefore,
        windowAfter,
        allUsers,
        assignedUsers: allUsers ? [] : selectedUsers,
        assignedGroups: allUsers ? [] : selectedGroups,
        enabled,
        nextDueDate,
        lastCompleted: null,
        updatedAt: Timestamp.now()
      };

      if (editingSchedule) {
        const scheduleRef = doc(db, `companies/${companyId}/forms/${formId}/schedules/${editingSchedule.id}`);
        await updateDoc(scheduleRef, scheduleData);
      } else {
        const schedulesRef = collection(db, `companies/${companyId}/forms/${formId}/schedules`);
        await addDoc(schedulesRef, {
          ...scheduleData,
          createdAt: Timestamp.now()
        });
      }

      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error('Error saving schedule:', err);
      alert('Failed to save schedule: ' + err.message);
    }
  }

  async function handleDelete(scheduleId) {
    if (!window.confirm('Delete this schedule? This cannot be undone.')) return;

    try {
      const scheduleRef = doc(db, `companies/${companyId}/forms/${formId}/schedules/${scheduleId}`);
      await deleteDoc(scheduleRef);
    } catch (err) {
      console.error('Error deleting schedule:', err);
      alert('Failed to delete schedule: ' + err.message);
    }
  }

  async function toggleEnabled(schedule) {
    try {
      const scheduleRef = doc(db, `companies/${companyId}/forms/${formId}/schedules/${schedule.id}`);
      await updateDoc(scheduleRef, {
        enabled: !schedule.enabled,
        updatedAt: Timestamp.now()
      });
    } catch (err) {
      console.error('Error toggling schedule:', err);
      alert('Failed to update schedule: ' + err.message);
    }
  }

  if (loading) {
    return <div className="p-4 text-gray-600 dark:text-gray-400">Loading schedules...</div>;
  }

  if (!formId) {
    return (
      <div className="p-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 text-center">
          <div className="text-4xl mb-3">💾</div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Save Form First
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Please save this form before adding schedules. Schedules can only be created for saved forms.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Check Schedules</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Automate when this check needs to be completed
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="bg-rose-700 hover:bg-rose-800 text-white px-4 py-2 rounded-md shadow-sm font-medium"
        >
          ➕ Add Schedule
        </button>
      </div>

      {/* Schedule List */}
      {schedules.length === 0 && !showForm ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
          <p className="text-gray-600 dark:text-gray-400 mb-4">No schedules configured</p>
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="text-rose-700 dark:text-rose-400 hover:underline"
          >
            Create your first schedule
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {schedules.map(schedule => {
            const freqLabel = SCHEDULE_FREQUENCIES.find(f => f.value === schedule.frequency)?.label || 'Custom';
            const assignmentText = schedule.allUsers 
              ? 'All Users' 
              : `${schedule.assignedUsers?.length || 0} user(s), ${schedule.assignedGroups?.length || 0} group(s)`;

            return (
              <div
                key={schedule.id}
                className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {freqLabel}
                        {schedule.frequency === 'custom' && ` (${schedule.customDays} days)`}
                      </h3>
                      <span className={`px-2 py-1 text-xs rounded ${schedule.enabled ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}>
                        {schedule.enabled ? 'Active' : 'Paused'}
                      </span>
                    </div>
                    
                    {schedule.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {schedule.description}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <div>
                        📅 Next due: <span className="font-medium text-gray-900 dark:text-white">
                          {schedule.nextDueDate ? new Date(schedule.nextDueDate.toDate()).toLocaleDateString() : 'Not set'}
                        </span>
                      </div>
                      <div>
                        👥 {assignmentText}
                      </div>
                      <div>
                        ⏰ Window: {schedule.windowBefore || 0} days before, {schedule.windowAfter || 0} days after
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleEnabled(schedule)}
                      className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white px-3 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      title={schedule.enabled ? 'Pause' : 'Resume'}
                    >
                      {schedule.enabled ? '⏸️' : '▶️'}
                    </button>
                    <button
                      onClick={() => handleEdit(schedule)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 px-3 py-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(schedule.id)}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 px-3 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Schedule Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {editingSchedule ? 'Edit Schedule' : 'New Schedule'}
              </h3>

              <div className="space-y-4">
                {/* Frequency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Frequency
                  </label>
                  <select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {SCHEDULE_FREQUENCIES.map(freq => (
                      <option key={freq.value} value={freq.value}>
                        {freq.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Custom Days */}
                {frequency === 'custom' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Repeat Every (days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={customDays}
                      onChange={(e) => setCustomDays(parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                )}

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description (optional)
                  </label>
                  <input
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="e.g., Weekly safety inspection"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {editingSchedule ? 'Next Due Date' : 'First Due Date'}
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                {/* Acceptance Window */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Window Before (days)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={windowBefore}
                      onChange={(e) => setWindowBefore(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Window After (days)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={windowAfter}
                      onChange={(e) => setWindowAfter(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
                  Users can complete the check within this window around the due date
                </p>

                {/* Assignment */}
                <div>
                  <label className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={allUsers}
                      onChange={(e) => setAllUsers(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Assign to all users
                    </span>
                  </label>

                  {!allUsers && (
                    <>
                      {/* Select Users */}
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Assigned Users
                        </label>
                        <div className="max-h-40 overflow-y-auto border dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-900/50 p-2">
                          {users.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">No users found</p>
                          ) : (
                            users.map(user => (
                              <label key={user.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedUsers.includes(user.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedUsers([...selectedUsers, user.id]);
                                    } else {
                                      setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                                    }
                                  }}
                                  className="rounded"
                                />
                                <span className="text-sm text-gray-900 dark:text-white">
                                  {user.name || user.email}
                                </span>
                              </label>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Select Groups */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Assigned Groups
                        </label>
                        <div className="max-h-40 overflow-y-auto border dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-900/50 p-2">
                          {groups.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">No groups found</p>
                          ) : (
                            groups.map(group => (
                              <label key={group.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedGroups.includes(group.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedGroups([...selectedGroups, group.id]);
                                    } else {
                                      setSelectedGroups(selectedGroups.filter(id => id !== group.id));
                                    }
                                  }}
                                  className="rounded"
                                />
                                <span className="text-sm text-gray-900 dark:text-white">
                                  {group.name}
                                </span>
                              </label>
                            ))
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Enabled */}
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => setEnabled(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Schedule enabled
                    </span>
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex-1 bg-rose-700 hover:bg-rose-800 text-white px-4 py-2 rounded-md shadow-sm font-medium"
                >
                  {editingSchedule ? 'Update' : 'Create'} Schedule
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
